import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyL } from '#src/atoms/typography/index.tsx';
import { Video } from '#src/atoms/ui/Video/index.tsx';
import { useConnectionContext } from '#src/contexts/connection/index.tsx';
import { glossary } from '#src/lib/glossary.ts';

import poster from './assets/poster.webp';
import av1 from './assets/stage.av1.webm';
import h264 from './assets/stage.h264.mp4';
import vp9 from './assets/stage.vp9.webm';

const Root = styled.section`
  width: 100%;
  height: 70vh;
  display: grid;
  align-items: center;
  justify-items: center;
  isolation: isolate;
  position: relative;
  &::before {
    position: absolute;
    inset: 0;
    z-index: 1;
    content: '';
    background:
      linear-gradient(180deg, rgba(16, 16, 16, 0) 75%, #101010 100%),
      radial-gradient(
        50% 50% at 50% 50%,
        rgba(0, 0, 0, 0.6) 0%,
        rgba(0, 0, 0, 0) 100%
      ),
      linear-gradient(180deg, #101010 0%, rgba(16, 16, 16, 0) 17%);
  }
  > * {
    grid-row-start: 1;
    grid-column-start: 1;
    overflow: hidden;
  }
`;
const Header = styled(Vertical)`
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: var(--quadruple-unit);
  text-align: center;
  width: 100%;
`;
const Title = styled.h1`
  font-size: var(--fs-landing-title);
`;

const defaultSources = [
  { src: av1, type: 'video/webm; codecs="av01.0.31M.08,opus"' },
  { src: vp9, type: 'video/webm; codecs="vp9"' },
  { src: h264 },
];

type Source = {
  src: string;
  type?: string;
};

type Props = {
  league?: ReactNode;
  title: ReactNode;
  subtitle: ReactNode;
  sources?: Source[];
  videoPoster?: string;
};
export const HeroVideo = ({
  league,
  title,
  subtitle,
  sources = defaultSources,
  videoPoster = poster,
}: Props) => {
  const { signUp } = useConnectionContext();

  return (
    <Root>
      <Video
        sources={sources}
        autoPlay
        playsInline
        loop
        muted
        hideMutedIcon
        poster={videoPoster}
      />
      <Header center>
        {league}
        <div>
          <Title>{title}</Title>
          <BodyL>{subtitle}</BodyL>
        </div>

        <Button color="secondary" size="large" onClick={signUp}>
          <FormattedMessage {...glossary.playNow} />
        </Button>
      </Header>
    </Root>
  );
};
